import React from 'react';
import styled, { css, keyframes } from 'styled-components';

const shimmer = keyframes`
    0%   { background-position: 150% 0; }
    100% { background-position: -50% 0; }
`;

export type SkeletonVariant = 'block' | 'cards' | 'form';

const Part = styled.span`
    display: block;
    border-radius: var(--island-skeleton-r, ${({ theme }) => theme.primitives?.radius?.md ?? '1rem'});
    background-color: rgba(0, 0, 0, 0.01);
    background-image: linear-gradient(
        90deg,
        transparent 0%,
        rgba(255, 255, 255, 0.02) 50%,
        transparent 100%
    );
    background-size: 200% 100%;
    background-repeat: no-repeat;
    animation: ${shimmer} 1.4s ease-in-out infinite;

    @media (prefers-reduced-motion: reduce) {
        animation: none;
    }
`;

const cardsLayout = css`
    > ${Part} {
        height: var(--island-skeleton-card-h, 24rem);
    }

    @media (min-width: 768px) {
        grid-template-columns: repeat(var(--island-skeleton-cols, 2), minmax(0, 1fr));
        gap: 3rem;
    }
`;

const formLayout = css`
    > ${Part} {
        height: 6rem;
    }

    > ${Part}:first-child {
        height: 5rem;
        width: min(32rem, 70%);
        justify-self: center;
        margin-bottom: 2rem;
    }

    > ${Part}:nth-child(4) {
        height: 16rem;
    }

    > ${Part}:last-child {
        width: min(25rem, 100%);
        justify-self: center;
        margin-top: 1rem;
    }
`;

const Wrap = styled.span<{ $variant: SkeletonVariant }>`
    display: grid;
    gap: 2rem;
    width: var(--island-skeleton-w, 100%);

    ${({ $variant }) => $variant === 'cards' && cardsLayout}
    ${({ $variant }) => $variant === 'form' && formLayout}
    ${({ $variant }) => $variant === 'block' && css`
        > ${Part} {
            height: var(--island-skeleton-h, 8rem);
        }
    `}
`;

interface Props {
    variant?: SkeletonVariant;
    parts?: number;
}

/**
 * Скелетон-заглушка внутри shadow root — React-двойник
 * `.react-island__skeleton` из `api/resources/css/bx/islands.css`.
 *
 * Зачем отдельный компонент, если ванильная заглушка уже есть в blade:
 * у shadow-острова light-DOM дети host'а перестают рендериться в момент
 * `attachShadow`, а это происходит ДО того, как догрузится ленивый чанк
 * самого острова. Без фоллбека получалась дыра «скелетон исчез — контента
 * ещё нет — контент появился рывком». Поэтому ванильная заглушка закрывает
 * время до загрузки islands-бандла, а эта — от attachShadow до контента.
 *
 * Вариант силуэта и число плашек приходят из тех же data-атрибутов, что
 * рендерит `<x-react-island>` (см. `IslandsContainer`), а размеры — из тех же
 * CSS-переменных на host'е: custom properties наследуются через shadow-границу.
 * Вид продублирован в двух местах вынужденно — стили light-DOM внутрь shadow
 * не попадают; правишь силуэт здесь — правь и в islands.css.
 */
const IslandSkeleton: React.FC<Props> = ({ variant = 'block', parts = 1 }) => (
    <Wrap $variant={variant} aria-hidden="true">
        {Array.from({ length: Math.max(parts, 1) }, (_, i) => (
            <Part key={i} />
        ))}
    </Wrap>
);

export default IslandSkeleton;
